import { useRuntimeConfig } from '#app'

export interface UnityInstance {
  SendMessage(objectName: string, methodName: string, value: string | number | boolean): void

  Quit(): Promise<void>
}

export interface UnityConfig {
  arguments: any[]
  dataUrl: string
  frameworkUrl: string
  codeUrl: string
  streamingAssetsUrl: string
  companyName: string
  productName: string
  productVersion: string
  showBanner: (msg: any, type: any) => void
  cacheControl: (url: string) => void
}

export interface UnityMessage {
  id: number
  time_stamp: string
  msg: any
}

export interface UnityBuildManifest {
  loaderUrl: string
  frameworkUrl: string
  dataUrl: string
  codeUrl: string
}

declare global {
  interface Window {
    unityInstance?: UnityInstance
  }
}

declare function createUnityInstance(
  canvas: HTMLCanvasElement,
  config: UnityConfig,
  onProgress?: (progress: number) => void,
): Promise<UnityInstance>

declare const unityProfiler: {
  createButton(instance: UnityInstance): HTMLElement
  shutDown(): void
}

let editorWebSocket: InstanceType<typeof globalThis.WebSocket> | null = null

function initEditorWebSocket() {
  if (editorWebSocket) return editorWebSocket

  console.log('🔌 Connecting to Unity Editor WebSocket...')

  editorWebSocket = new WebSocket('ws://localhost:8080/unity')

  editorWebSocket.addEventListener('open', () => {
    console.log('✅ WebSocket connected to Unity Editor!')

    const initMessage = {
      name: 'InitParams',
      id: 2,
      time_stamp: new Date().toISOString().split('.')[0] + 'Z',
      msg: {
        platform: 'Web',
        browser: navigator.userAgent,
        environment: 'Development',
      },
    }

    editorWebSocket!.send(JSON.stringify(initMessage))
    console.log('📤 Sent InitParams to Unity:', initMessage)

    window.postMessage({ name: 'UnityEditorReady' }, '*')
  })

  // ✅ Add message listener to receive data FROM Unity
  editorWebSocket.addEventListener('message', event => {
    try {
      const data = JSON.parse(event.data)
      console.log('📥 Received from Unity:', data)

      // Forward Unity messages to the window message system
      // so your app can handle them the same way as WebGL
      window.postMessage(
        {
          name: data.name,
          id: data.id,
          time_stamp: data.time_stamp,
          msg: data.msg,
        },
        '*',
      )
    } catch (error) {
      console.error('❌ Failed to parse Unity message:', error, event.data)
    }
  })

  editorWebSocket.addEventListener('error', error => {
    console.error('❌ WebSocket error:', error)
  })

  editorWebSocket.addEventListener('close', () => {
    console.log('🔌 WebSocket disconnected')
    editorWebSocket = null
  })

  return editorWebSocket
}

function attachUnityProfilerButton(instance: UnityInstance) {
  if (typeof unityProfiler === 'undefined' || !unityProfiler.createButton) {
    console.warn('❌ unityProfiler missing')
    return
  }

  const btn = unityProfiler.createButton(instance)
  btn.style.position = 'fixed'
  btn.style.right = '0px'
  btn.style.top = '50%'
  btn.style.transform = 'translateY(-50%)'
  btn.style.zIndex = '2147483647'
  btn.style.color = 'rgb(0, 0, 0)'

  document.body.appendChild(btn)

  window.addEventListener('beforeunload', () => {
    unityProfiler.shutDown?.()
  })
}

function loadScript(src: string): Promise<void> {
  return new Promise((resolve, reject) => {
    const s = document.createElement('script')
    s.src = src
    s.onload = () => resolve()
    s.onerror = () => reject(new Error(`Failed to load script: ${src}`))
    document.body.appendChild(s)
  })
}

/**
 * Loads the Unity build manifest file (build-manifest.json) that contains hashed filenames.
 * Unity generates this manifest during the build process with content-hashed filenames like:
 * - loaderUrl: "3bf32abfaa1c80c59a4613e6957f42e7.loader.js"
 * - frameworkUrl: "5f9af22ea65668e3069772726d7517af.framework.js"
 * - dataUrl: "c677b0202b3ed2e865c785e01fb5322d.data"
 * - codeUrl: "1ba899a12138baa791dfc5931facf29a.wasm"
 *
 * The manifest is always fetched fresh (no-store) to prevent stale manifest from pointing
 * at build files that no longer exist after a new deployment.
 * If the manifest file is not found, falls back to default filenames without hashes.
 */
async function loadBuildManifest(baseUrl: string): Promise<UnityBuildManifest | null> {
  try {
    const manifestUrl = `${baseUrl}/build-manifest.json`
    const response = await fetch(manifestUrl, {
      cache: 'no-store', // Always fetch fresh to avoid stale manifest pointing at missing build files
    })

    if (!response.ok) {
      if (response.status === 404) {
        console.warn('⚠️ build-manifest.json not found (404), falling back to default filenames')
      } else {
        console.warn(
          `⚠️ Failed to load build-manifest.json (HTTP ${response.status} ${response.statusText}) from ${response.url || manifestUrl}, falling back to default filenames`
        )
      }
      return null
    }

    const manifest = await response.json()
    console.log('✅ Loaded Unity build manifest:', manifest)

    // Validate manifest has required fields
    if (!manifest.loaderUrl || !manifest.frameworkUrl || !manifest.dataUrl || !manifest.codeUrl) {
      console.warn('⚠️ Invalid manifest format, missing required fields')
      return null
    }

    return manifest
  } catch (error) {
    console.warn('⚠️ Failed to load build manifest:', error)
    return null
  }
}

export default async (canvas: HTMLCanvasElement): Promise<UnityInstance> => {
  const env = useRuntimeConfig()
  const enableUnityProfiler = env.public.USE_UNITY_PROFILER === 'true'

  if (env.public.USE_UNITY_EDITOR === 'true') {
    initEditorWebSocket()

    return {
      SendMessage(objectName: string, methodName: string, value: string | number | boolean) {
        if (editorWebSocket!.readyState === WebSocket.OPEN) {
          let messageData
          try {
            messageData = typeof value === 'string' ? JSON.parse(value) : value
          } catch {
            console.error('Failed to parse message value:', value)
            return
          }

          const payload = {
            name: methodName,
            id: messageData.id || 1,
            time_stamp: messageData.time_stamp || new Date().toISOString(),
            msg: messageData.msg || messageData,
          }

          editorWebSocket!.send(JSON.stringify(payload))
          console.log('📤 Sent to Unity:', methodName, messageData)
        } else {
          console.warn('⚠️ WebSocket not ready, state:', editorWebSocket!.readyState)
        }
      },
      async Quit() {
        editorWebSocket?.close()
      },
    }
  }

  const UNITY_BUILD_URL = `${env.public.UNITY_BUILD_BASE_URL}/${env.public.UNITY_BUILD_VERSION}`
  const UNITY_PROFILER_URL = `${env.public.UNITY_BUILD_BASE_URL}/TemplateData/profiler.js`

  // Load build manifest to get hashed filenames
  console.log('📦 Fetching Unity build manifest from:', `${UNITY_BUILD_URL}/build-manifest.json`)
  const manifest = await loadBuildManifest(UNITY_BUILD_URL)

  // Use manifest filenames if available, otherwise fall back to default names
  const loaderUrl = manifest?.loaderUrl || 'build.loader.js'
  const frameworkUrl = manifest?.frameworkUrl || 'build.framework.js'
  const dataUrl = manifest?.dataUrl || 'build.data'
  const codeUrl = manifest?.codeUrl || 'build.wasm'

  console.log('🎮 Unity build files:', { loaderUrl, frameworkUrl, dataUrl, codeUrl })

  // Only use immutable caching when filenames differ from the defaults — i.e. they are content-hashed.
  // If the manifest points at the same names as the defaults, caching immutably could prevent recovery
  // after a re-publish under the same version path.
  const isContentHashed =
    loaderUrl !== 'build.loader.js' ||
    frameworkUrl !== 'build.framework.js' ||
    dataUrl !== 'build.data' ||
    codeUrl !== 'build.wasm'

  const UNITY_LOADER_URL = `${UNITY_BUILD_URL}/${loaderUrl}`

  function unityShowBanner(msg: any, type: any) {
    console.log('🎮 Unity Banner [' + type + ']:', msg)
    if (type === 'error') {
      console.error('🚨 Unity Error:', msg)
    } else if (type === 'warning') {
      console.warn('⚠️ Unity Warning:', msg)
    }
  }

  const config = {
    arguments: [],
    dataUrl: `${UNITY_BUILD_URL}/${dataUrl}`,
    frameworkUrl: `${UNITY_BUILD_URL}/${frameworkUrl}`,
    codeUrl: `${UNITY_BUILD_URL}/${codeUrl}`,
    streamingAssetsUrl: 'StreamingAssets',
    companyName: 'VRIFY',
    productName: '3D viewer',
    productVersion: '0.1.0',
    showBanner: unityShowBanner,
    cacheControl: function (_url: string) {
      // Unity files with hashed names can be cached immutably
      if (isContentHashed && (_url.match(/\.data/) || _url.match(/\.wasm/) || _url.match(/\.js/))) {
        return 'immutable'
      }
      // Legacy cache control for non-hashed builds
      if (_url.match(/\.data/) || _url.match(/\.bundle/)) {
        return 'must-revalidate'
      }
      if (_url.match(/mapbox.*\.png/)) {
        return 'immutable'
      }
      return 'no-store'
    },
  }

  // Ensure loader exists
  console.log('📥 Loading Unity loader script:', UNITY_LOADER_URL)
  if (!('createUnityInstance' in window)) {
    await loadScript(UNITY_LOADER_URL)
  }

  // Load profiler.js only when profiling enabled
  if (enableUnityProfiler) {
    try {
      await loadScript(UNITY_PROFILER_URL)
    } catch (e) {
      console.warn(`Failed to load Unity profiler script: ${UNITY_PROFILER_URL}`, e)
    }
  }

  const instance = await createUnityInstance(canvas, config)
  window.unityInstance = instance

  if (enableUnityProfiler) {
    attachUnityProfilerButton(instance)
  }

  return instance
}
